import { useEffect, useState, Suspense, lazy } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { PageLoader } from './components/PageLoader';
import { moduleRoutes } from './modules/registry';
import { useModuleEnabled } from './hooks/usePublicConfig';

// Core 404 (not a module) — lazy so it costs nothing on the happy path.
const NotFound = lazy(() => import('./pages/NotFound'));
const ModuleUnavailable = lazy(() => import('./components/ModuleUnavailable'));
// Fill My Bow fundraiser — fork-local standalone page (full-bleed, own chrome).
const FillMyBow = lazy(() => import('./pages/FillMyBow'));

/** Tier-2 runtime gate for public module routes. Optimistic while config loads;
 *  the admin module is never gated (lockout prevention). */
function RuntimeModuleGate({ moduleId, children }: { moduleId: string; children: JSX.Element }) {
  const enabled = useModuleEnabled(moduleId);
  if (!enabled) return <ModuleUnavailable />;
  return children;
}

/**
 * Main Application Component
 *
 * Provides the core application structure with routing, header, and footer.
 * Fork lockdown: the only core route is '/' (the fundraiser). Installed-module
 * routes (admin, payments) compose from the registry; the template's WP
 * '/:slug' page engine is deliberately not mounted (see src/modules/README.md).
 *
 * @returns {JSX.Element} The main app component with routing configuration
 */
export const App = () => {
  // Application initialization state
  const [isLoading, setIsLoading] = useState(true);
  const { pathname, hash } = useLocation();
  const isAdminRoute = pathname === '/admin' || pathname.startsWith('/admin/');
  // Full-bleed routes render their own chrome (admin precedent + campaign pages).
  // This fork: the fundraiser IS the home page, so '/' is bare too.
  const isBareRoute = isAdminRoute || pathname === '/' || pathname === '/fill-my-bow';

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  useEffect(() => {
    setIsLoading(false);
  }, []);

  // Show loading spinner during app initialization
  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div className="min-h-screen flex flex-col" data-testid="app-container">
      {/* Site header with navigation (hidden on full-bleed routes) */}
      {!isBareRoute && <Header />}

      {/* Main content area with routing */}
      <main className="flex-grow" data-testid="main-content">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* This fork: the Fill My Bow fundraiser IS the home page.
                (The template's WP DynamicPage home is intentionally unrouted at
                '/'; single-segment WP slugs still resolve via /:slug below.) */}
            <Route path="/" element={<FillMyBow />} />
            <Route path="/fill-my-bow" element={<FillMyBow />} />

            {/* Module routes — rendered BEFORE the /:slug WP catch-all (which
                would otherwise swallow single-segment paths like /admin).
                v6 ranking also guarantees static segments outrank /:slug. */}
            {moduleRoutes.map(({ path, moduleId, Component }) => (
              <Route
                key={path}
                path={path}
                element={
                  <RuntimeModuleGate moduleId={moduleId}>
                    <Component />
                  </RuntimeModuleGate>
                }
              />
            ))}

            {/* Fork lockdown: the WP /:slug catch-all (DynamicPage) is
                intentionally unrouted — this fork exposes ONLY the fundraiser
                plus /admin and capability-token pages. Everything else 404s. */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>

      {/* Site footer (hidden on full-bleed routes) */}
      {!isBareRoute && <Footer />}
    </div>
  );
};
